import React, { useContext, useEffect } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import i18n from "i18next";
import {
  buildLocalizedPathname,
  isValidLocale,
  KivuI18nContext,
  LOCALE
} from "@kivunova/kivufrontendcommon";
import FirstVisitDownloadDialog from "./FirstVisitDownloadDialog/FirstVisitDownloadDialog";
import { isShowFirstVisitDownloadDialogEnabled } from "../Utils/firstVisitDownloadDialogEnv";
import {
  clearUserLocaleSwitch,
  peekUserLocaleSwitch
} from "../Utils/localeNavigation";

const persistLocale = (locale: LOCALE) => {
  try {
    localStorage.setItem("lang", locale);
    localStorage.setItem("i18nextLng", locale);
  } catch {
    /* ignore quota / private mode */
  }
};

const LanguageLayout: React.FC = () => {
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const showFirstVisitDialog = isShowFirstVisitDownloadDialogEnabled();

  const i18nContext = useContext(KivuI18nContext);
  const setLanguage = i18nContext?.setLanguage;
  const language = i18nContext?.language;
  const defaultLang = i18nContext?.defaultLang;

  useEffect(() => {
    if (!setLanguage || !defaultLang || !language) return;

    const urlLocale = isValidLocale(lang) ? lang : undefined;
    const pathForContext = buildLocalizedPathname(
      location.pathname,
      language,
      defaultLang
    );
    const pendingUserLocale = peekUserLocaleSwitch();

    if (pendingUserLocale) {
      if (language !== pendingUserLocale) {
        // Abandon a stale selector switch when the user navigated to another locale URL.
        if (urlLocale && urlLocale !== language) {
          clearUserLocaleSwitch();
        } else {
          return;
        }
      } else if (pathForContext !== location.pathname) {
        navigate(
          {
            pathname: pathForContext,
            search: location.search,
            hash: location.hash
          },
          { replace: true }
        );
        if (i18n.language !== language) {
          i18n.changeLanguage(language);
        }
        persistLocale(language);
        return;
      } else {
        clearUserLocaleSwitch();
      }
    }

    // Explicit URL locale wins over stale context (bookmarks, shared links, in-app nav).
    if (urlLocale && language !== urlLocale) {
      setLanguage(urlLocale);
      return;
    }

    if (pathForContext !== location.pathname) {
      navigate(
        {
          pathname: pathForContext,
          search: location.search,
          hash: location.hash
        },
        { replace: true }
      );
      if (i18n.language !== language) {
        i18n.changeLanguage(language);
      }
      persistLocale(language);
      return;
    }

    const resolvedLocale = urlLocale ?? language;

    if (i18n.language !== resolvedLocale) {
      i18n.changeLanguage(resolvedLocale);
    }
    persistLocale(resolvedLocale);
  }, [
    lang,
    language,
    defaultLang,
    location.pathname,
    location.search,
    location.hash,
    navigate,
    setLanguage
  ]);

  return (
    <>
      {showFirstVisitDialog && <FirstVisitDownloadDialog />}
      <Outlet />
    </>
  );
};

export default LanguageLayout;
